import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCommonTranslation } from "@/services/culture/common.translations";
import { useModuleRights } from "./useModuleRights";
import { useAppStore } from "@/store/appStore";
import { queries } from "@/services/platformNotifications";
import type { Notification } from "@/store/platformNotificationsStore";
import type { IdNameViewModel } from "@vintecc/capture-api-types/v1";
import { useCompanyId } from "./useCompanyId";

interface BuildPlatformWarningsArgs {
  blobstoreData: IdNameViewModel[] | undefined;
  apiTokenData: IdNameViewModel[] | undefined;
  canManageBlobstore: boolean;
  canManageApiTokens: boolean;
  t: ReturnType<typeof useCommonTranslation>["t"];
}

const buildPlatformWarnings = ({
  blobstoreData,
  apiTokenData,
  canManageBlobstore,
  canManageApiTokens,
  t,
}: BuildPlatformWarningsArgs): Notification[] => {
  const warnings: Notification[] = [];

  blobstoreData?.forEach((issue) => {
    warnings.push({
      id: `blobstore-token-${issue.id}`,
      title: t("home.warnings.blobstoreTokens.title"),
      message: canManageBlobstore ? t("home.warnings.blobstoreTokens.message", { count: 1 }) : t("home.warnings.blobstoreTokens.contactAdmin"),
      url: canManageBlobstore ? "/storage-hub/capture-storage/blob/$id/settings" : undefined,
      params: { id: issue.id },
      intent: "warning",
      removable: false,
    });
  });

  apiTokenData?.forEach((issue) => {
    warnings.push({
      id: `api-token-${issue.id}`,
      title: t("home.warnings.apiTokens.title"),
      message: canManageApiTokens ? t("home.warnings.apiTokens.message", { count: 1 }) : t("home.warnings.apiTokens.contactAdmin"),
      url: canManageApiTokens ? "/identity-and-access/api-tokens/$id" : undefined,
      params: { id: issue.id },
      intent: "warning",
      removable: false,
    });
  });

  return warnings;
};

export const usePlatformWarnings = (): Notification[] => {
  const { t } = useCommonTranslation();
  const { can } = useModuleRights();

  const loginAnswer = useAppStore((s) => s.loginAnswer);
  const companyId = useCompanyId();

  const isAuthenticated = loginAnswer !== null;

  const canViewBlobstore = can("view", "Databases");
  const canManageBlobstore = can("manage", "Databases");
  const canViewApiTokens = can("view", "ApiTokens");
  const canManageApiTokens = can("manage", "ApiTokens");

  const { data: blobstoreData } = useQuery({
    ...queries.getBlobstoreTokenWarnings(companyId),
    enabled: isAuthenticated && canViewBlobstore && companyId !== undefined,
  });

  const { data: apiTokenData } = useQuery({
    ...queries.getApiTokenWarnings(companyId),
    enabled: isAuthenticated && canViewApiTokens && companyId !== undefined,
  });

  return useMemo(
    () => buildPlatformWarnings({ blobstoreData, apiTokenData, canManageBlobstore, canManageApiTokens, t }),
    [blobstoreData, apiTokenData, canManageBlobstore, canManageApiTokens, t],
  );
};
